import React, { useState, useEffect } from 'react';
import authManager from '../utils/authManager.js';
import '../styles/AuthScreen.css';

const AuthScreen = ({ onAuthenticated }) => {
  const [authStep, setAuthStep] = useState('register'); // register, login, security_setup, nfc_upgrade
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    // Registration data
    name: '',
    nationality: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptNews: false,
    acceptTerms: false,

    // Security setup
    pin: '',
    securityMethod: '' // 'pin' or 'biometric'
  });

  useEffect(() => {
    // Check if user is returning (has account)
    const existingUser = authManager.getCurrentUser();
    if (existingUser) {
      setAuthStep('login');
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleEmailLogin = async () => {
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await authManager.loginWithPassword(formData.email, formData.password);

      if (result.success) {
        setSuccess('Login successful! 🎉');
        setTimeout(() => onAuthenticated(result.user), 1000);
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }

    setLoading(false);
  };

  const handleRegister = async () => {
    // Validation
    if (!formData.name || !formData.nationality || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!formData.acceptTerms) {
      setError('Please accept the Terms of Service');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create account with full user data
      const userData = {
        name: formData.name,
        nationality: formData.nationality,
        email: formData.email,
        acceptNews: formData.acceptNews,
        registeredAt: new Date().toISOString()
      };

      const result = await authManager.register(formData.email, formData.password, formData.name, userData);

      if (result.success) {
        setCurrentUser(result.user);
        setSuccess('✅ Account created successfully! 📧 Welcome email sent!');

        // Move to security setup
        setTimeout(() => {
          setSuccess('Setting up your security options...');
          setTimeout(() => {
            setAuthStep('security_setup');
          }, 1000);
        }, 2000);

      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }

    setLoading(false);
  };

  const handleNFCAuth = async () => {
    setLoading(true);
    setError('');
    setSuccess('📱 Please tap your NFC device...');

    try {
      const result = await authManager.loginWithNFC();

      if (result.success) {
        setSuccess('NFC Authentication successful! 🎉');
        setTimeout(() => onAuthenticated(result.user), 1000);
      } else {
        setError(result.error || 'NFC authentication failed');
        setSuccess('');
      }
    } catch (error) {
      setError('NFC not supported or failed');
      setSuccess('');
    }

    setLoading(false);
  };

  const handleNFCRegister = async () => {
    setLoading(true);
    setError('');
    setSuccess('📱 Please tap your NFC device to register...');

    try {
      const result = await authManager.registerNFC();

      if (result.success) {
        setSuccess(`✅ NFC tag registered! (ID: ${result.nfcId.substring(0, 8)}...)`);
        setTimeout(() => {
          setAuthMode('login');
          setSuccess('You can now login with your NFC tag!');
        }, 2000);
      } else {
        setError(result.error || 'NFC registration failed');
        setSuccess('');
      }
    } catch (error) {
      setError('NFC registration failed');
      setSuccess('');
    }

    setLoading(false);
  };

  const handleBiometricAuth = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await authManager.loginWithBiometric();

      if (result.success) {
        setSuccess('Biometric authentication successful! 🎉');
        setTimeout(() => onAuthenticated(result.user), 1000);
      } else {
        setError(result.error || 'Biometric authentication failed');
      }
    } catch (error) {
      setError('Biometric authentication not available');
    }

    setLoading(false);
  };

  const handlePINAuth = async () => {
    if (!formData.pin || formData.pin.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await authManager.loginWithPin(formData.pin);

      if (result.success) {
        setSuccess('PIN authentication successful! 🎉');
        setTimeout(() => onAuthenticated(result.user), 1000);
      } else {
        setError(result.error || 'Invalid PIN');
      }
    } catch (error) {
      setError('PIN authentication failed');
    }

    setLoading(false);
  };

  const handleSecuritySetup = async () => {
    if (formData.securityMethod === 'pin') {
      if (!formData.pin || formData.pin.length !== 4) {
        setError('Please enter a 4-digit PIN');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const result = await authManager.setPin(formData.pin);

        if (result.success) {
          setSuccess('🔐 PIN security enabled!');
          setTimeout(() => {
            setAuthStep('nfc_upgrade');
          }, 1500);
        } else {
          setError(result.error || 'Failed to set PIN');
        }
      } catch (error) {
        setError('Failed to set PIN');
      }

      setLoading(false);

    } else if (formData.securityMethod === 'biometric') {
      setLoading(true);
      setError('');

      try {
        const result = await authManager.loginWithBiometric();

        if (result.success) {
          setSuccess('👆 Biometric security enabled!');
          setTimeout(() => {
            setAuthStep('nfc_upgrade');
          }, 1500);
        } else {
          setError(result.error || 'Biometric setup failed');
        }
      } catch (error) {
        setError('Biometric setup failed');
      }

      setLoading(false);
    }
  };

  const handleSkipSecurity = () => {
    setAuthStep('nfc_upgrade');
  };

  const renderRegisterScreen = () => (
    <div className="auth-container">
      <div className="auth-header">
        <div className="auth-logo">✈️</div>
        <h1>Join DIAS Travel</h1>
        <p>Your comprehensive travel companion</p>
      </div>

      <div className="auth-form">
        <input
          type="text"
          placeholder="Full Name *"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          disabled={loading}
        />

        <select
          value={formData.nationality}
          onChange={(e) => handleInputChange('nationality', e.target.value)}
          disabled={loading}
          style={{
            width: '100%',
            padding: '16px',
            border: '2px solid #f0f0f0',
            borderRadius: '12px',
            marginBottom: '16px',
            fontSize: '16px',
            color: formData.nationality ? '#000' : '#999'
          }}
        >
          <option value="">Select Nationality *</option>
          <option value="Spanish">Spanish</option>
          <option value="French">French</option>
          <option value="German">German</option>
          <option value="Italian">Italian</option>
          <option value="Portuguese">Portuguese</option>
          <option value="Dutch">Dutch</option>
          <option value="Belgian">Belgian</option>
          <option value="British">British</option>
          <option value="American">American</option>
          <option value="Canadian">Canadian</option>
          <option value="Australian">Australian</option>
          <option value="Other">Other</option>
        </select>

        <input
          type="email"
          placeholder="Email Address *"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          disabled={loading}
        />

        <input
          type="password"
          placeholder="Password (min 6 characters) *"
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          disabled={loading}
        />

        <input
          type="password"
          placeholder="Confirm Password *"
          value={formData.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
          disabled={loading}
        />

        <div className="checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.acceptTerms}
              onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
              disabled={loading}
            />
            <span>I accept the Terms of Service and Privacy Policy *</span>
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.acceptNews}
              onChange={(e) => handleInputChange('acceptNews', e.target.checked)}
              disabled={loading}
            />
            <span>📧 I'd like to receive travel tips and updates from DIAS (optional)</span>
          </label>
        </div>

        <button
          className="auth-btn primary"
          onClick={handleRegister}
          disabled={loading}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </div>

      <div className="auth-footer">
        <p>Already have an account? <button onClick={() => setAuthStep('login')}>Sign In</button></p>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {success && <div className="auth-success">{success}</div>}
    </div>
  );

  const renderLoginScreen = () => (
    <div className="auth-container">
      <div className="auth-header">
        <button className="back-btn" onClick={() => setAuthStep('register')}>←</button>
        <h2>Welcome Back</h2>
      </div>

      <div className="auth-form">
        <input
          type="email"
          placeholder="Email address"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          disabled={loading}
        />

        <input
          type="password"
          placeholder="Password"
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          disabled={loading}
        />

        <button
          className="auth-btn primary"
          onClick={handleEmailLogin}
          disabled={loading}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </button>
      </div>

      <div className="auth-footer">
        <p>Don't have an account? <button onClick={() => setAuthStep('register')}>Sign Up</button></p>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {success && <div className="auth-success">{success}</div>}
    </div>
  );

  const renderSecuritySetupScreen = () => (
    <div className="auth-container">
      <div className="auth-header">
        <div className="security-icon">🔐</div>
        <h2>Secure Your Travel Data</h2>
        <p>This app can store sensitive documents like passports and boarding passes. Let's add extra security.</p>
      </div>

      <div className="security-options">
        <div className="security-info">
          <div className="info-item">
            <span className="info-icon">📄</span>
            <span>Store travel documents securely</span>
          </div>
          <div className="info-item">
            <span className="info-icon">🆔</span>
            <span>Keep ID photos and boarding passes</span>
          </div>
          <div className="info-item">
            <span className="info-icon">🔒</span>
            <span>Protect personal information</span>
          </div>
        </div>

        <h3>Choose your security method:</h3>

        <div className="security-methods">
          <button
            className={`security-method-btn ${formData.securityMethod === 'pin' ? 'selected' : ''}`}
            onClick={() => handleInputChange('securityMethod', 'pin')}
            disabled={loading}
          >
            <span className="method-icon">🔢</span>
            <div>
              <div className="method-name">4-Digit PIN</div>
              <div className="method-desc">Quick and secure</div>
            </div>
          </button>

          <button
            className={`security-method-btn ${formData.securityMethod === 'biometric' ? 'selected' : ''}`}
            onClick={() => handleInputChange('securityMethod', 'biometric')}
            disabled={loading}
          >
            <span className="method-icon">👆</span>
            <div>
              <div className="method-name">Biometric</div>
              <div className="method-desc">Fingerprint or Face ID</div>
            </div>
          </button>
        </div>

        {formData.securityMethod === 'pin' && (
          <div className="pin-setup">
            <input
              type="password"
              placeholder="Enter 4-digit PIN"
              value={formData.pin}
              onChange={(e) => handleInputChange('pin', e.target.value.replace(/\D/g, '').slice(0, 4))}
              maxLength="4"
              disabled={loading}
              className="pin-input"
            />
          </div>
        )}

        {formData.securityMethod && (
          <button
            className="auth-btn primary"
            onClick={handleSecuritySetup}
            disabled={loading || (formData.securityMethod === 'pin' && formData.pin.length !== 4)}
          >
            {loading ? 'Setting up...' : 'Enable Security'}
          </button>
        )}

        <button
          className="auth-btn secondary"
          onClick={handleSkipSecurity}
          disabled={loading}
        >
          Skip for Now
        </button>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {success && <div className="auth-success">{success}</div>}
    </div>
  );

  const renderNFCUpgradeScreen = () => (
    <div className="auth-container">
      <div className="auth-header">
        <div className="nfc-icon">🎫</div>
        <h2>Unlock Premium Features</h2>
        <p>Got DIAS merchandise? Scan your NFC tag to unlock all premium features!</p>
      </div>

      <div className="premium-features">
        <h3>Premium includes:</h3>
        <div className="feature-list">
          <div className="feature-item">
            <span className="feature-icon">📄</span>
            <span>Document Storage & Management</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🧳</span>
            <span>Smart Luggage & Packing Lists</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">📊</span>
            <span>PDF Trip Reports & Export</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🏆</span>
            <span>Prize Challenges & Rewards</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">☁️</span>
            <span>Cloud Sync & Backup</span>
          </div>
        </div>

        <button
          className="auth-btn primary nfc-btn"
          onClick={handleNFCAuth}
          disabled={loading}
        >
          <span className="nfc-icon-btn">📱</span>
          {loading ? 'Scanning...' : 'Scan NFC Tag'}
        </button>

        <button
          className="auth-btn secondary"
          onClick={() => onAuthenticated(currentUser)}
          disabled={loading}
        >
          Continue with Free Version
        </button>

        <div className="upgrade-info">
          <p><small>Don't have DIAS merchandise? <a href="#" style={{color: '#b8a082'}}>Get yours here!</a></small></p>
        </div>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {success && <div className="auth-success">{success}</div>}
    </div>
  );

  const renderNFCScreen = () => (
    <div className="auth-container">
      <div className="auth-header">
        <button className="back-btn" onClick={() => setAuthMode('welcome')}>←</button>
        <h2>NFC Authentication</h2>
      </div>

      <div className="nfc-info">
        <div className="nfc-icon">📱</div>
        <p>Use your NFC-enabled device, card, or tag to authenticate</p>
      </div>

      <div className="auth-form">
        <button
          className="auth-btn primary"
          onClick={handleNFCAuth}
          disabled={loading}
        >
          {loading ? 'Scanning...' : '📡 Scan NFC Device'}
        </button>

        <div className="auth-divider">
          <span>Don't have NFC set up?</span>
        </div>

        <button
          className="auth-btn secondary"
          onClick={handleNFCRegister}
          disabled={loading}
        >
          Register New NFC Device
        </button>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {success && <div className="auth-success">{success}</div>}
    </div>
  );

  const renderPINScreen = () => (
    <div className="auth-container">
      <div className="auth-header">
        <button className="back-btn" onClick={() => setAuthMode('welcome')}>←</button>
        <h2>PIN Authentication</h2>
      </div>

      <div className="pin-info">
        <div className="pin-icon">🔢</div>
        <p>Enter your 4-digit PIN</p>
      </div>

      <div className="auth-form">
        <input
          type="password"
          placeholder="4-digit PIN"
          value={formData.pin}
          onChange={(e) => handleInputChange('pin', e.target.value.replace(/\D/g, '').slice(0, 4))}
          maxLength="4"
          disabled={loading}
          className="pin-input"
        />

        <button
          className="auth-btn primary"
          onClick={handlePINAuth}
          disabled={loading || formData.pin.length !== 4}
        >
          {loading ? 'Verifying...' : 'Unlock with PIN'}
        </button>

        <div className="auth-divider">
          <span>Don't have a PIN set up?</span>
        </div>

        <button
          className="auth-btn secondary"
          onClick={handleSetPIN}
          disabled={loading || formData.pin.length !== 4}
        >
          Set New PIN
        </button>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {success && <div className="auth-success">{success}</div>}
    </div>
  );

  return (
    <div className="auth-screen">
      {authStep === 'register' && renderRegisterScreen()}
      {authStep === 'login' && renderLoginScreen()}
      {authStep === 'security_setup' && renderSecuritySetupScreen()}
      {authStep === 'nfc_upgrade' && renderNFCUpgradeScreen()}
    </div>
  );
};

export default AuthScreen;